import { useState, useMemo, useEffect } from "react";
import { NavLink, useLocation, useNavigate, matchPath } from "react-router-dom";
import { useFeatures } from "../../utils/featureContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis,
    faAngleLeft,
    faChevronDown,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./Sidebar.css";
import LoadingOverlay from "../loadingoverlay/LoadingOverlay";

const hasFeatureAccess = (itemFeature, userFeatures = []) => {
    if (!itemFeature) return true;

    // single feature
    if (typeof itemFeature === "string") {
        return userFeatures.includes(itemFeature);
    }

    // multiple features → OR condition
    if (Array.isArray(itemFeature)) {
        return itemFeature.some((f) => userFeatures.includes(f));
    }

    return false;
};

const filterMenuByFeatures = (menuItems, userFeatures) => {
    return menuItems
        .map((item) => {
            if (Array.isArray(item.children) && item.children.length > 0) {
                const filteredChildren = filterMenuByFeatures(
                    item.children,
                    userFeatures
                );

                if (filteredChildren.length === 0) return null;

                return { ...item, children: filteredChildren };
            }

            return hasFeatureAccess(item.feature, userFeatures) ? item : null;
        })
        .filter(Boolean);
};

const findFirstRoute = (menuItems) => {
    for (const item of menuItems) {
        if (item.children?.length) {
            const childRoute = findFirstRoute(item.children);
            if (childRoute) return childRoute;
        }

        if (item.to) {
            return item.to;
        }
    }
    return null;
};

export default function Sidebar({
    items = [],
    preserveSearch = true,
    className = "",
    defaultCollapsed = false,
}) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [openMenus, setOpenMenus] = useState({});
    const location = useLocation();
    const suffix = preserveSearch ? location.search : "";

    const { features, loading } = useFeatures();

    const navigate = useNavigate();

    const toggleMenu = (key) =>
        setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));

    const parentHasActiveChild = (item) => {
        if (!item?.children) return false;

        return item.children.some((child) => {
            if (!child.to) return false;
            return location.pathname.includes(`/${child.to}`);
        });
    };

    const filteredItems = useMemo(
        () => filterMenuByFeatures(items, features),
        [items, features]
    );


    const renderItems = (menuItems, level = 0) => (
        <ul className={`ad-submenu ad-submenu-level-${level}`} role={level === 0 ? "menu" : "group"}>
            {menuItems.map((item) => {
                const key = item.to || item.label;
                const linkTo = `${item.default ? "." : item.to || "#"}${suffix}`;
                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const isOpen = openMenus[key] || parentHasActiveChild(item);
                const showChildren = hasChildren && isOpen && !collapsed;

                return (
                    <li key={key} className="ad-submenu-item">
                        <div className={`ad-item-wrap ${hasChildren ? "has-children" : ""}`}>
                            {hasChildren ? (
                                <button
                                    type="button"
                                    className={`ad-item ad-btn-item ${item.cName || ""}`}
                                    onClick={() => toggleMenu(key)}
                                    title={collapsed ? item.label : undefined}
                                    aria-expanded={isOpen}
                                >
                                    {item.icon && <span className="ad-item-ic">{item.icon}</span>}
                                    {!collapsed && <span className="ad-item-label">{item.label}</span>}
                                    {!collapsed && (
                                        <span
                                            role="img"
                                            aria-hidden="true"
                                            className={`submenu-toggle ${isOpen ? "open" : ""}`}
                                        >
                                            <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
                                        </span>
                                    )}
                                </button>
                            ) : (
                                <NavLink
                                    to={linkTo}
                                    end
                                    className={({ isActive }) =>
                                        `ad-item ${item.cName || ""}${isActive ? " active" : ""}`
                                    }
                                    title={collapsed ? item.label : undefined}
                                >
                                    {item.icon && <span className="ad-item-ic">{item.icon}</span>}
                                    {!collapsed && <span className="ad-item-label">{item.label}</span>}
                                </NavLink>
                            )}
                        </div>
                        {showChildren && renderItems(item.children, level + 1)}
                    </li>
                );
            })}
        </ul>
    );

    if (loading) return <LoadingOverlay />;

    if (filteredItems.length === 0) return null;

    return (
        <aside
            className={`ad-sidebar ${collapsed ? "collapsed" : ""} ${className}`}
            aria-label="Section navigation"
        >
            <div className="ad-side-header">
                {!collapsed && <div className="ad-side-title">Menu</div>}
                <button
                    type="button"
                    className="btn-transperant btn-margin"
                    onClick={() => setCollapsed((v) => !v)}
                    aria-pressed={collapsed}
                    aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    title={collapsed ? "Expand" : "Collapse"}
                >
                    {collapsed ? <FontAwesomeIcon icon={faEllipsis} /> : <FontAwesomeIcon icon={faAngleLeft} />}
                </button>
            </div>

            <nav className="ad-menu" role="navigation" aria-label="Sidebar menu">
                {renderItems(filteredItems)}
            </nav>
        </aside>
    );
}
